<!-- EditMemberAccountForm.vue -->
<!-- Modal de edição de permissões granulares de um vínculo Membro-Titular. -->
<!-- Exibe uma grade: recursos (Textos, Notas, Atividades) × ações (Ver, Criar, Editar, Excluir) -->
<!-- com toggles individuais. Salva via PATCH /api/managed-accounts/{id}/permissions. -->
<script setup>
import { ref, reactive, computed, watch, onMounted, onUnmounted } from "vue";
import { useManagedAccountStore } from "@/stores/useManagedAccountStore";
import * as managedAccountService from "@/services/managedAccountService";
import { useDraggableModal } from "@/composables/useDraggableModal";
import PerfilCombobox from "./PerfilCombobox.vue";

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

// ── Props & Emits ─────────────────────────────────────────────────────────────
const props = defineProps({
  relationshipId: { type: Number, required: true },
});

const emit = defineEmits(["close", "saved"]);

// ── Store ─────────────────────────────────────────────────────────────────────
const managedAccountStore = useManagedAccountStore();

// ── Estado ───────────────────────────────────────────────────────────────────
const isSaving = ref(false);
const isDirty = ref(false);
const errorMsg = ref(null);

// ====================================================================
// ✅ PATCH: TOAST PADRÃO DE SUCESSO/ERRO
// ====================================================================
// Mesmo padrão usado nos demais formulários: 3000ms de duração,
// fechamento/ação final após 2400ms e botão "x" posicionado via CSS.
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

// Ao salvar com sucesso, esconde só o painel do modal; o toast (num Teleport
// próprio) segue visível sozinho até o onFinish (emit saved → pai fecha) desmontar.
const hideBackdropForSuccess = ref(false);

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);

  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message, onFinish) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();

  if (typeof onFinish === "function") {
    // Esconde o painel do modal já, o toast (num Teleport próprio) segue
    // visível sozinho até o onFinish (emit saved) desmontar o componente.
    hideBackdropForSuccess.value = true;
  }

  setTimeout(() => {
    if (typeof onFinish === "function") onFinish();
  }, 2400);
}

// [PATCH] relationshipType agora é string livre, sem enum, sem dropdown
const relationshipType = ref("");

// Nome do membro (buscado da lista paginada)
const memberName = computed(() => {
  const rel = managedAccountStore.pagedRelationships.find(
    (r) => r.managedAccountRelationshipId === props.relationshipId
  );
  return rel?.fullName ?? rel?.userName ?? `Vínculo #${props.relationshipId}`;
});

// ── Permissões padrão do sistema ──────────────────────────────────────────────
function buildDefaultPermissions() {
  return {
    texts: { view: true, create: false, edit: false, delete: false },
    notes: { view: true, create: true, edit: true, delete: true },
    activities: { view: true, create: false, edit: false, delete: false },
  };
}

// ── Estado local da grade ─────────────────────────────────────────────────────
const perms = reactive(buildDefaultPermissions());

// ── Opção global "Somente conteúdo do titular" (RestrictToTitular no backend) ──
// Quando ligada: o membro só cria/edita/exclui NOTAS em atividades criadas pelo
// titular; não cria/edita/exclui textos nem atividades. Ver/consultar não muda.
// Compõe o padrão inicial (recomendada para o cenário professor → aluno).
const restrictToTitular = ref(true);

// ── Delegação administrativa (CanManageCircle no backend) ─────────────────────
// Quando ligada, o membro cadastra e vincula contas em nome do titular, é o que
// permite ao professor ou ao administrativo montar turma e cadastrar aluno. As
// contas criadas nascem vinculadas ao titular, não a ele.
const canManageCircle = ref(false);

// ── Molde do tipo de vínculo ─────────────────────────────────────────────────
// Marcado, salva esta mesma configuração como padrão do perfil ("Professor",
// "Aluno"...). Vale para todo membro daquele tipo sem permissões próprias,
// inclusive os que forem cadastrados depois, a política vira uma edição, e
// não uma por membro.
const saveAsTypeTemplate = ref(false);

const templateTypeName = computed(() => {
  const raw = (relationshipType.value || "").trim();
  if (!raw) return null;
  return raw.charAt(0).toUpperCase() + raw.slice(1);
});

// Moldes já definidos pelo titular. Servem para a tela dizer QUAL padrão este
// membro segue quando ele não tem permissões próprias, sem isso, "padrão"
// ficava ambíguo entre o do sistema e o do tipo.
const typeTemplates = ref([]);

const hasTypeTemplate = computed(() =>
  typeTemplates.value.some(
    (t) =>
      (t.relationshipType || "").toLowerCase() ===
      (templateTypeName.value || "").toLowerCase()
  )
);

// Nome do padrão que vale para este membro quando ele não tem grade própria.
const inheritedDefaultLabel = computed(() =>
  hasTypeTemplate.value && templateTypeName.value
    ? `padrão do perfil «${templateTypeName.value}»`
    : "padrão do sistema"
);

// Rótulo do botão que descarta a grade própria: precisa dizer para onde volta,
// com o nome do padrão, senão "o padrão" fica ambíguo entre perfil e sistema.
const followDefaultLabel = computed(
  () => `Restaurar o ${inheritedDefaultLabel.value}`
);

async function loadTypeTemplates() {
  try {
    const response = await managedAccountService.fetchPermissionTemplates();
    typeTemplates.value = response.data ?? [];
  } catch (error) {
    console.warn(
      "[EditMemberAccountForm] Falha ao carregar os padrões por tipo:",
      error.response?.data?.message || error.message
    );
    typeTemplates.value = [];
  }
}

// Funde uma grade parcial (do molde do tipo, ou de um perfil escolhido no
// combobox) sobre o padrão do sistema, mesma lógica usada tanto pelo que a
// tela mostra para quem não tem permissão própria quanto pelo preview do
// PerfilCombobox ao trocar de perfil (ver applyPermissionsGrid). `source`
// null (sem molde, ou perfil sem preset conhecido) devolve o padrão puro.
function buildGridFromSource(source) {
  const grid = buildDefaultPermissions();
  const base = {
    ...grid,
    restrictToTitular: true,
    canManageCircle: false,
  };

  if (!source) return base;

  for (const resource of ["texts", "notes", "activities"]) {
    for (const action of ["view", "create", "edit", "delete"]) {
      base[resource][action] =
        source?.[resource]?.[action] ?? grid[resource][action];
    }
  }

  base.restrictToTitular = source.restrictToTitular ?? true;
  base.canManageCircle = source.canManageCircle ?? false;
  return base;
}

// Regra que vale para quem NÃO tem permissões próprias: o molde do tipo, se o
// titular definiu um; senão o padrão do sistema. É a referência da tela inteira
//, o que a grade mostra, o que "Seguir o padrão" restaura e o que decide se há
// customização a salvar.
const inheritedDefaultGrid = computed(() => {
  const template = typeTemplates.value.find(
    (t) =>
      (t.relationshipType || "").toLowerCase() ===
      (templateTypeName.value || "").toLowerCase()
  );
  return buildGridFromSource(template?.permissions);
});

// Traz a grade da tela para a regra herdada (molde do tipo ou padrão do sistema).
function applyInheritedDefault() {
  applyPermissionsGrid(inheritedDefaultGrid.value, { alreadyMerged: true });
}

// Aplica uma grade nos toggles da tela. `source` pode ser a grade JÁ mesclada
// de inheritedDefaultGrid (alreadyMerged: true) ou uma grade parcial vinda do
// combobox de Perfil (mescla sobre o padrão do sistema, igual a um molde).
function applyPermissionsGrid(source, { alreadyMerged = false } = {}) {
  const base = alreadyMerged ? source : buildGridFromSource(source);

  for (const resource of ["texts", "notes", "activities"]) {
    for (const action of ["view", "create", "edit", "delete"]) {
      perms[resource][action] = base[resource][action];
    }
  }

  restrictToTitular.value = base.restrictToTitular;
  canManageCircle.value = base.canManageCircle;
  // watch(restrictToTitular) só dispara em MUDANÇA de valor, chamar direto
  // cobre o caso de a grade nova já nascer com restrictToTitular igual à
  // anterior, mas com create/edit/delete de texto/atividade inconsistentes.
  applyRestrictLock();
}

// ── Sincronizar a grade ao trocar de Perfil ──────────────────────────────────
// Ver PerfilCombobox.vue: "select" só dispara numa escolha deliberada da
// lista (não a cada tecla) e chega ANTES do valor do campo mudar, então
// isUsingDefault aqui ainda compara contra o perfil ANTIGO, é exatamente o
// que queremos saber: "esse membro já tinha permissão própria?". Só nesse
// caso a grade fica intocada, sem pergunta, sem substituição automática: a
// customização já feita para o membro não é mexida só por trocar o texto.
function handlePerfilSelected({ permissions }) {
  if (!isUsingDefault.value) return;
  applyPermissionsGrid(permissions);
  markDirty();
}

// Células do grid que a opção torna inócuas (Leitura Y): escrita de textos e
// atividades. Ficam desligadas e desabilitadas enquanto a opção estiver ligada.
function isCellLocked(resourceKey, actionKey) {
  return (
    restrictToTitular.value &&
    (resourceKey === "texts" || resourceKey === "activities") &&
    actionKey !== "view"
  );
}

// Força off as células travadas (mantém a UI coerente com o enforcement do backend).
function applyRestrictLock() {
  if (!restrictToTitular.value) return;
  for (const resource of ["texts", "activities"]) {
    for (const action of ["create", "edit", "delete"]) {
      perms[resource][action] = false;
    }
  }
}

watch(restrictToTitular, () => applyRestrictLock());

onMounted(async () => {
  document.addEventListener("keydown", handleKeydown);

  const rel = managedAccountStore.pagedRelationships.find(
    (r) => r.managedAccountRelationshipId === props.relationshipId
  );

  if (rel) {
    // [PATCH] carrega o valor string diretamente
    relationshipType.value = rel.relationshipType ?? "";
  }

  // Antes de decidir o que mostrar: o vínculo sem permissões próprias segue o
  // molde do tipo, e é o molde que precisa aparecer na grade, mostrar o padrão
  // do sistema no lugar dele exibiria uma regra que não é a que vale.
  await loadTypeTemplates();

  if (!rel?.permissions) {
    applyInheritedDefault();
  }

  if (rel?.permissions) {
    const parsed =
      typeof rel.permissions === "string"
        ? JSON.parse(rel.permissions)
        : rel.permissions;

    const def = buildDefaultPermissions();
    for (const resource of ["texts", "notes", "activities"]) {
      for (const action of ["view", "create", "edit", "delete"]) {
        perms[resource][action] =
          parsed?.[resource]?.[action] ?? def[resource][action];
      }
    }

    // Opção global, vem do flag dedicado (coluna no backend).
    // Ausência do flag → padrão inicial (ligada).
    restrictToTitular.value = parsed?.restrictToTitular ?? true;

    // Delegação, flag dedicado; ausência significa desligada.
    canManageCircle.value = parsed?.canManageCircle ?? false;
  }

  // Mantém a UI coerente com o enforcement (trava células de Textos/Atividades).
  applyRestrictLock();
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);

  // ====================================================================
  // ✅ PATCH: limpa timeout do toast ao desmontar o componente
  // ====================================================================
  if (toastTimeoutId.value) {
    clearTimeout(toastTimeoutId.value);
    toastTimeoutId.value = null;
  }
});

// ── Keyboard ──────────────────────────────────────────────────────────────────
function handleKeydown(e) {
  // ESC fecha (já existente)
  if (e.key === "Escape") {
    emit("close");
    return;
  }

  // ENTER salva (NOVO)
  if (e.key === "Enter") {
    // Evita submit duplo ou estado inválido
    if (isSaving.value || !isDirty.value) return;

    // Evita comportamento padrão (ex: submit implícito, focus jump)
    e.preventDefault();

    handleSave();
  }
}

// ── Detecção de mudança ───────────────────────────────────────────────────────
function markDirty() {
  isDirty.value = true;
  errorMsg.value = null;
}

// ── Verificar se está usando o padrão do sistema ─────────────────────────────
// "Sem permissões próprias" = a grade da tela é idêntica à regra herdada (molde
// do tipo, quando existe; senão o padrão do sistema). Nesse caso o formulário
// grava NULL e o vínculo continua acompanhando o padrão quando ele mudar.
const isUsingDefault = computed(() => {
  const base = inheritedDefaultGrid.value;

  if (restrictToTitular.value !== base.restrictToTitular) return false;
  if (canManageCircle.value !== base.canManageCircle) return false;

  for (const resource of ["texts", "notes", "activities"]) {
    for (const action of ["view", "create", "edit", "delete"]) {
      if (perms[resource][action] !== base[resource][action]) return false;
    }
  }
  return true;
});

// ── Selo ao lado do Perfil ───────────────────────────────────────────────────
// "Perfil" acumula dois sentidos: o papel da pessoa ("é professor") e a
// origem das permissões dela. Ao customizar a grade, o primeiro continua
// valendo e o segundo não, sem este selo, a tela mostrava só "Professor" e
// dava a entender que a grade ainda vinha do molde. Mesmo vocabulário da
// coluna Permissões em MemberAccountForm.vue (Padrão/Personalizado), para a
// pessoa não ter de aprender dois nomes para o mesmo estado.
const permissionSourceBadge = computed(() =>
  isUsingDefault.value
    ? { label: "Padrão", icon: "fas fa-lock-open", modifier: "default" }
    : { label: "Personalizado", icon: "fas fa-lock", modifier: "custom" }
);

// ── Resetar para o padrão ─────────────────────────────────────────────────────
// Descarta as permissões próprias e volta a seguir a regra herdada.
function resetToDefault() {
  applyInheritedDefault();
  isDirty.value = true;
  errorMsg.value = null;
}

// ── Salvar ────────────────────────────────────────────────────────────────────
// ── Salvar ────────────────────────────────────────────────────────────────────
async function handleSave() {
  if (isSaving.value || !isDirty.value) return;

  isSaving.value = true;
  errorMsg.value = null;

  try {
    // [PATCH] primeira letra maiúscula; fallback "Colaborador" se vazio
    const raw = relationshipType.value.trim() || "Colaborador";
    const normalized = raw.charAt(0).toUpperCase() + raw.slice(1);

    await managedAccountStore.updateRelationship(props.relationshipId, {
      relationshipType: normalized,
    });

    const payload = isUsingDefault.value
      ? null
      : {
          texts: { ...perms.texts },
          notes: { ...perms.notes },
          activities: { ...perms.activities },
          restrictToTitular: restrictToTitular.value,
          canManageCircle: canManageCircle.value,
        };

    // Ao salvar como molde do tipo, a grade vira o padrão de todo mundo
    // daquele perfil, então o vínculo que a originou também deve passar a
    // HERDAR o molde, e não guardar uma cópia individual idêntica. Sem isso,
    // esse vínculo ficava "Personalizado" para sempre (mesmo com a mesma
    // permissão efetiva de quem entra depois herdando o molde) e parava de
    // acompanhar o molde se ele fosse editado mais tarde.
    const savingAsTemplate =
      saveAsTypeTemplate.value && !!payload && !!templateTypeName.value;

    // Molde primeiro: se isso falhar, o catch abaixo interrompe o salvamento
    // e o vínculo mantém a permissão própria que já tinha, não fica sem
    // nenhuma das duas.
    if (savingAsTemplate) {
      await managedAccountService.upsertPermissionTemplate(
        templateTypeName.value,
        payload
      );
    }

    await managedAccountStore.updatePermissions(
      props.relationshipId,
      savingAsTemplate ? null : payload
    );

    isDirty.value = false;

    // ====================================================================
    // ✅ PATCH: toast de sucesso antes de emitir "saved"
    // ====================================================================
    // Se o pai fechar o modal ao receber "saved", o atraso permite que o
    // usuário veja o toast antes do componente ser desmontado.
    showSuccessToast("Vínculo atualizado com sucesso.", () => {
      emit("saved");
    });
  } catch (e) {
    const userFriendlyMessage =
      e?.response?.data?.message || e?.message || "Erro ao salvar vínculo.";

    // Mantém o banner atual e adiciona o toast padronizado.
    errorMsg.value = userFriendlyMessage;
    showErrorToast(userFriendlyMessage);
  } finally {
    isSaving.value = false;
  }
}

// ── Dados da grade ─────────────────────────────────────────────────────────────
const RESOURCES = [
  { key: "texts", label: "Textos", icon: "fas fa-file-alt" },
  { key: "notes", label: "Notas", icon: "fas fa-sticky-note" },
  { key: "activities", label: "Atividades", icon: "fas fa-tasks" },
];

const ACTIONS = [
  { key: "view", label: "Visualizar", icon: "fas fa-eye" },
  { key: "create", label: "Criar", icon: "fas fa-plus-circle" },
  { key: "edit", label: "Editar", icon: "fas fa-pencil-alt" },
  { key: "delete", label: "Excluir", icon: "fas fa-trash-alt" },
];
</script>

<template>
  <!-- Overlay -->
  <div
    v-show="!hideBackdropForSuccess"
    class="modal-overlay"
    @click.self="emit('close')"
  >
    <div
      ref="panelRef"
      class="modal-panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby="perm-title"
      :style="panelStyle"
    >
      <!-- ── Header ────────────────────────────────────────────────────────── -->
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <div class="header-left">
          <div class="header-icon">
            <i class="fas fa-shield-alt"></i>
          </div>
          <div>
            <h2 class="modal-title">Editar Vínculo</h2>
            <p class="modal-subtitle">
              {{ memberName }}
              <span v-if="templateTypeName" class="subtitle-profile"
                >({{ templateTypeName }})</span
              >
            </p>
          </div>
        </div>
        <button class="btn-close" @click="emit('close')" title="Fechar">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Sobra um único aviso aqui: o perfil não ter molde próprio ainda, que
           é o que faz "Padrão" no selo significar padrão do SISTEMA e não do
           perfil. Os outros dois estados (segue o molde / tem grade própria)
           já são ditos pelo selo ao lado do campo Perfil, logo abaixo. -->
      <p v-if="isUsingDefault && !hasTypeTemplate" class="inherit-banner">
        <i class="fas fa-info-circle"></i>
        <!-- Um único nó de texto: com vários filhos, o flex do container
             quebraria a frase em colunas. -->
        <span>
          Este membro <strong>segue o padrão do sistema</strong>. O perfil «{{
            templateTypeName
          }}» ainda não tem padrão próprio.
        </span>
      </p>

      <!-- Perfil do membro, combobox com moldes já criados e perfis
           sugeridos de escola (ver PerfilCombobox.vue). -->
      <div class="relationship-type-row">
        <div class="section-heading">
          <span class="section-title">Perfil</span>
          <span class="section-desc">
            O papel deste membro (ex.: aluno, professor, coordenador).
          </span>
        </div>
        <div class="relationship-type-field">
          <PerfilCombobox
            v-model="relationshipType"
            class="relationship-type-input"
            placeholder="Ex: aluno, professor, filho…"
            :disabled="isSaving"
            @update:model-value="markDirty"
            @molde-saved="loadTypeTemplates"
            @select="handlePerfilSelected"
          />
          <span
            class="source-badge"
            :class="`source-badge--${permissionSourceBadge.modifier}`"
            :title="
              isUsingDefault
                ? `As permissões deste membro vêm do ${inheritedDefaultLabel}.`
                : `Este membro tem permissões próprias, que prevalecem sobre o ${inheritedDefaultLabel}.`
            "
          >
            <i :class="permissionSourceBadge.icon" class="source-badge__icon"></i>
            {{ permissionSourceBadge.label }}
          </span>
        </div>
      </div>

      <!-- ── Grade de permissões ───────────────────────────────────────────── -->
      <div class="modal-body">
        <div class="section-heading">
          <span class="section-title">Permissões por recurso</span>
          <span class="section-desc">
            Ligue as ações que este membro poderá realizar. Deixar tudo no padrão
            faz o vínculo seguir o {{ inheritedDefaultLabel }}.
          </span>
        </div>

        <table class="perm-table">
          <thead>
            <tr>
              <th class="th-resource">Recurso</th>
              <th v-for="action in ACTIONS" :key="action.key" class="th-action">
                <span class="action-header">
                  <i :class="action.icon" class="action-icon"></i>
                  {{ action.label }}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="resource in RESOURCES"
              :key="resource.key"
              class="perm-row"
            >
              <td class="td-resource">
                <span class="resource-label">
                  <i :class="resource.icon" class="resource-icon"></i>
                  {{ resource.label }}
                </span>
              </td>
              <td v-for="action in ACTIONS" :key="action.key" class="td-toggle">
                <label
                  class="toggle-wrap"
                  :class="{ 'toggle-wrap--locked': isCellLocked(resource.key, action.key) }"
                >
                  <input
                    type="checkbox"
                    class="toggle-input"
                    v-model="perms[resource.key][action.key]"
                    @change="markDirty"
                    :disabled="isCellLocked(resource.key, action.key)"
                    :title="
                      isCellLocked(resource.key, action.key)
                        ? 'Desligado por «Trabalhar apenas no conteúdo do seu círculo»'
                        : `${action.label} ${resource.label}`
                    "
                  />
                  <span class="toggle-track">
                    <span class="toggle-thumb"></span>
                  </span>
                </label>
              </td>
            </tr>
          </tbody>
        </table>

      </div>

      <div class="modal-scroll">
        <div class="section-heading section-heading--spaced">
          <span class="section-title">Escopo e delegação</span>
          <span class="section-desc">
            Onde este membro pode trabalhar e o que ele pode administrar em seu
            nome.
          </span>
        </div>

        <div class="restrict-row">
          <label class="toggle-wrap restrict-toggle">
            <input
              type="checkbox"
              class="toggle-input"
              v-model="restrictToTitular"
              @change="markDirty"
            />
            <span class="toggle-track">
              <span class="toggle-thumb"></span>
            </span>
          </label>
          <div class="restrict-texts">
            <span class="restrict-title">
              Trabalhar apenas no conteúdo do seu círculo
              <span class="restrict-context">(para alunos)</span>
            </span>
            <span class="restrict-hint">
              Ligada, o membro <strong>cria notas somente</strong> em atividades
              preparadas por você ou pelos seus outros membros, e edita ou
              exclui apenas as notas dele, nessas mesmas atividades.
            </span>
          </div>
        </div>

        <!-- ── Delegação: administrar o círculo do titular ─────────────────── -->
        <div class="restrict-row">
          <label class="toggle-wrap restrict-toggle">
            <input
              type="checkbox"
              class="toggle-input"
              v-model="canManageCircle"
              @change="markDirty"
            />
            <span class="toggle-track">
              <span class="toggle-thumb"></span>
            </span>
          </label>
          <div class="restrict-texts">
            <span class="restrict-title">
              Pode cadastrar contas e montar grupos
              <span class="restrict-context"
                >(para coordenador ou administrativo)</span
              >
            </span>
            <span class="restrict-hint">
              Ligada, o membro cadastra e vincula contas a
              <strong>você</strong> enquanto estiver logado no seu contexto,
              elas nascem vinculadas a você, e consomem o seu plano.
            </span>
            <span class="restrict-hint">
              Ele também revoga e altera as permissões de qualquer conta do
              círculo, não só das que ele mesmo cadastrou.
            </span>
          </div>
        </div>

        <!-- ── Molde do tipo de vínculo ────────────────────────────────────── -->
        <div v-if="templateTypeName" class="restrict-row">
          <label
            class="toggle-wrap restrict-toggle"
            :title="
              isUsingDefault
                ? 'Indisponível porque a grade está sem nenhuma alteração: mude alguma permissão para poder salvá-la como padrão do perfil.'
                : null
            "
          >
            <input
              type="checkbox"
              class="toggle-input"
              v-model="saveAsTypeTemplate"
              :disabled="isUsingDefault"
              @change="markDirty"
            />
            <span class="toggle-track">
              <span class="toggle-thumb"></span>
            </span>
          </label>
          <div class="restrict-texts">
            <span class="restrict-title">
              Salvar também como padrão do perfil «{{ templateTypeName }}»
            </span>
            <span class="restrict-hint">
              Esta configuração passa a valer para todo membro do perfil «{{
                templateTypeName
              }}» que não tenha permissões próprias, inclusive os que você
              cadastrar depois.
            </span>
          </div>
        </div>
      </div>

      <!-- ── Legenda ────────────────────────────────────────────────────────── -->
      <div class="modal-legend">
        <span class="legend-item">
          <span class="legend-dot legend-on"></span> Permitido
        </span>
        <span class="legend-item">
          <span class="legend-dot legend-off"></span> Bloqueado
        </span>
      </div>

      <!-- ── Erro (só ocupa espaço quando existe um) ─────────────────────────── -->
      <div v-if="errorMsg" class="banner-slot">
        <div class="error-banner">
          <i class="fas fa-exclamation-triangle"></i>
          {{ errorMsg }}
        </div>
      </div>

      <!-- ── Footer ────────────────────────────────────────────────────────── -->
      <div class="modal-footer">
        <button
          type="button"
          class="btn btn-ghost"
          @click="resetToDefault"
          :disabled="isSaving || isUsingDefault"
          :title="`Descartar as permissões próprias deste membro e voltar a seguir o ${inheritedDefaultLabel}`"
        >
          <i class="fas fa-undo"></i>
          {{ followDefaultLabel }}
        </button>

        <div class="footer-right">
          <button
            type="button"
            class="btn btn-secondary"
            @click="emit('close')"
            :disabled="isSaving"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="handleSave"
            :disabled="isSaving || !isDirty"
          >
            <i v-if="isSaving" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-save"></i>
            {{ isSaving ? "Salvando…" : "Salvar" }}
          </button>
        </div>
      </div>
    </div>
    </div>

  <!-- ====================================================================
       ✅ PATCH: TOAST PADRÃO DE SUCESSO/ERRO
       ==================================================================== -->
  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />

              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />

              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.modal-panel {
  max-width: 560px;
  min-height: 0;
}

.modal-header {
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.header-icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: var(--surface-3);
  color: var(--text-muted);
  border: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  flex-shrink: 0;
}

.modal-subtitle {
  font-size: 0.78rem;
  color: var(--text-muted);
  margin: 2px 0 0;
  font-weight: 500;
}

/* Perfil ao lado do nome: identifica de quem é a regra herdada em discussão. */
.subtitle-profile {
  color: #7c3aed;
  font-weight: 600;
}

.btn-close {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--text-subtle);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  transition: background 0.15s, color 0.15s;
}

.btn-close:hover {
  background: rgba(0, 0, 0, 0.1);
  color: var(--text-dark);
}

/* ── Cabeçalho de seção (tabela / opção) ─────────────────────────────────── */
.section-heading {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-bottom: 0.35rem;
}

.section-heading--spaced {
  margin-top: 0.65rem;
}

.section-title {
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--text-dark);
}

.section-desc {
  font-size: 0.74rem;
  line-height: 1.4;
  color: var(--text-muted);
}

/* Diz, antes de qualquer ajuste, qual regra vale hoje para este membro. */
.inherit-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  margin: 0.55rem 1.2rem 0;
  padding: 0.5rem 0.7rem;
  border-radius: 0.5rem;
  background-color: var(--info-bg);
  color: var(--info-text);
  font-size: 0.78rem;
  line-height: 1.35;
  flex-shrink: 0;
}

.inherit-banner i {
  margin-top: 0.1rem;
}

/* ── Perfil (modo full) ─────────────────────────────────────────── */
.relationship-type-row {
  display: flex;
  flex-direction: column;
  padding: 0.6rem 1.2rem 0.4rem;
  flex-shrink: 0;
}

/* Largura do combobox de Perfil (PerfilCombobox.vue), o resto do visual
   (borda, foco, estado desabilitado) é do próprio componente. */
.relationship-type-field {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.relationship-type-input {
  width: 40%;
  min-width: 160px;
}

/* Selo Padrão/Personalizado, mesmas cores e ícones da coluna Permissões em
   MemberAccountForm.vue, para os dois lugares falarem a mesma língua. */
.source-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: default;
}

.source-badge__icon {
  font-size: 0.65rem;
}

.source-badge--default {
  background: var(--neutral-200);
  color: var(--text-light);
}

.source-badge--custom {
  background: var(--brand-soft);
  color: var(--text-on-brand-soft);
}

/* ── Confirmação de substituição de permissões ao trocar de Perfil ───────── */
/* ── Banner de erro (só existe no DOM quando há um erro a mostrar) ───────── */
.banner-slot {
  flex-shrink: 0;
  padding: 0.5rem 1.2rem 0;
}

.default-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  width: 100%;
  padding: 0.55rem 0.85rem;
  background: var(--info-bg);
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  font-size: 0.78rem;
  color: var(--info-text);
  line-height: 1.4;
}

.banner-icon {
  flex-shrink: 0;
  margin-top: 1px;
}

.modal-body {
  flex-shrink: 0;
}

/* Escopo e delegação: segue o fluxo normal do modal, sem rolagem própria. */
.modal-scroll {
  padding: 0 1.2rem;
}

.perm-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.th-resource,
.th-action {
  padding: 0.22rem 0.5rem;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  border-bottom: 2px solid var(--neutral-200);
  white-space: nowrap;
}

.th-resource {
  text-align: left;
  width: 36%;
}

.th-action {
  text-align: center;
}

.action-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
}

.action-icon {
  font-size: 0.68rem;
  color: var(--text-dark);
}

.th-action:last-child .action-icon {
  color: var(--danger);
}

.perm-row {
  transition: background 0.12s;
}

.perm-row:hover {
  background: var(--surface-2);
}

.td-resource {
  padding: 0.26rem 0.5rem;
  border-bottom: 1px solid var(--border-color);
}

.resource-label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.76rem;
  font-weight: 600;
  color: var(--text-dark);
}

.resource-icon {
  width: 16px;
  text-align: center;
  color: var(--text-dark);
  font-size: 0.7rem;
}

.td-toggle {
  text-align: center;
  vertical-align: middle;
  /* Sem isso, o <td> reserva um "descendente" de texto invisível abaixo do
     toggle (como se fosse uma linha de texto normal), e isso empurra o
     centro visual para cima mesmo com vertical-align: middle. */
  line-height: 0;
  padding: 0.26rem 0.4rem;
  border-bottom: 1px solid var(--border-color);
}

/* ── Toggle switch ───────────────────────────────────────────────────────── */
.toggle-wrap {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  position: relative;
}

.toggle-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-track {
  position: relative;
  display: inline-block;
  width: 32px;
  height: 18px;
  background-color: var(--neutral-200);
  border-radius: 999px;
  transition: background-color 0.25s;
  flex-shrink: 0;
}

.toggle-thumb {
  position: absolute;
  height: 13px;
  width: 13px;
  left: 2.5px;
  top: 2.5px;
  background-color: var(--surface);
  border-radius: 50%;
  transition: transform 0.25s;
}

.toggle-input:checked + .toggle-track {
  background-color: var(--success);
}

.toggle-input:checked + .toggle-track .toggle-thumb {
  transform: translateX(14px);
}

.toggle-wrap:hover .toggle-track {
  filter: brightness(0.96);
}

/* Célula travada pela opção "Somente conteúdo do titular" */
.toggle-wrap--locked {
  cursor: not-allowed;
}

.toggle-input:disabled + .toggle-track {
  opacity: 0.4;
  cursor: not-allowed;
}

.toggle-wrap--locked:hover .toggle-track {
  filter: none;
}

/* ── Opção global: Somente conteúdo do titular ─────────────────────────────── */
/* As 3 linhas eram todas destacadas em âmbar, o que cancelava o próprio
   destaque (3 caixas "chamando atenção" ao mesmo tempo não chamam nenhuma).
   Agora ficam neutras. */
.restrict-row {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  margin-top: 0;
  padding: 0.4rem 0;
}

.restrict-toggle {
  flex-shrink: 0;
  margin-top: 1px;
}

.restrict-texts {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.restrict-title {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-dark);
}

/* Qualificador neutro entre parênteses, ex.: "(para alunos)". */
.restrict-context {
  font-weight: 400;
  color: var(--text-subtle);
}

.restrict-hint {
  font-size: 0.71rem;
  color: var(--text-muted);
  line-height: 1.4;
}

/* ── Legenda ─────────────────────────────────────────────────────────────── */
.modal-legend {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.4rem 1.2rem 0.5rem;
  font-size: 0.73rem;
  color: var(--text-muted);
  flex-wrap: wrap;
  flex-shrink: 0;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.legend-on {
  background: var(--success);
}
.legend-off {
  background: var(--neutral-200);
  border: 1px solid var(--neutral-400);
}
.legend-sep {
  color: var(--neutral-400);
}
.legend-note {
  color: var(--text-subtle);
}

/* ── Erro ────────────────────────────────────────────────────────────────── */
.error-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.55rem 0.85rem;
  background: var(--danger-bg);
  border: 1px solid var(--danger);
  border-radius: 10px;
  font-size: 0.78rem;
  color: var(--danger-text);
}

.modal-footer {
  justify-content: space-between;
  flex-wrap: wrap;
  flex-shrink: 0;
}

.footer-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-ghost {
  background: var(--neutral-200);
  color: var(--text-dark);
  border: 1px solid var(--neutral-400);
  /* O texto varia com o nome do perfil ("Restaurar o padrão do perfil
     «Administrador»") e pode ficar bem mais longo que "Cancelar"/"Salvar" —
     uma fonte menor aqui evita empurrar o rodapé para duas linhas. */
  font-size: 0.78rem;
}

.btn-ghost:not(:disabled):hover {
  background: var(--neutral-300);
  color: var(--text-dark);
}

</style>
